/**
 * Email allowlists for admin UI features.
 *
 * Source of truth: adminEmailAcces.json (next to config.json)
 *   e.g. https://api.artiumacademy.com/config/{env}/adminEmailAcces.json
 *
 * Edit emails only in that remote JSON — not in this file.
 *
 * Usage:
 *   import { useHasEmailAccess } from "../utils/emailAccessConfig";
 *   const { ready, allowed } = useHasEmailAccess("transactions");
 *   if (!ready) return <Spinner />;
 *   if (!allowed) return <AccessDenied />;
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { PROFILE_DATA_UPDATED_EVENT } from "./permissions";

export type EmailAccessTab =
  | "transactions"
  | "teacherLedger"
  | "teacherChange"
  | "rnr"
  | "demoOpsShiftEdit"
  | "demoShiftActive"
  | "viewChat"
  | "teacherFormRestricted"
  | "telephonyManager"
  | "telephonyOnly"
  | "outSourcingAnalytics"
  | "telephonyLogsCsv"
  | "demoOnlineDiscountAllowlist"
  | "demoOnlineDiscountExempt"
  | "LiveDemoAccessOnly";

/** Known tab keys — used when parsing remote JSON. */
export const EMAIL_ACCESS_TABS: readonly EmailAccessTab[] = [
  "transactions",
  "teacherLedger",
  "teacherChange",
  "rnr",
  "demoOpsShiftEdit",
  "demoShiftActive",
  "viewChat",
  "teacherFormRestricted",
  "telephonyManager",
  "telephonyOnly",
  "outSourcingAnalytics",
  "telephonyLogsCsv",
  "demoOnlineDiscountAllowlist",
  "demoOnlineDiscountExempt",
  "LiveDemoAccessOnly",
] as const;

type EmailAccessMap = Partial<Record<EmailAccessTab, readonly string[]>>;

/** Lists from adminEmailAcces.json (null until loaded). */
let remoteEmailAccess: EmailAccessMap | null = null;
let emailAccessVersion = 0;
const emailAccessListeners = new Set<() => void>();

const notifyEmailAccessListeners = () => {
  emailAccessVersion += 1;
  emailAccessListeners.forEach((listener) => listener());
};

/** Subscribe to remote list updates (used by useEmailAccessConfig). */
export const subscribeEmailAccess = (listener: () => void): (() => void) => {
  emailAccessListeners.add(listener);
  return () => emailAccessListeners.delete(listener);
};

export const getEmailAccessVersion = (): number => emailAccessVersion;

/** Normalize email for comparison. */
export const normalizeEmail = (email?: string | null): string =>
  String(email || "")
    .trim()
    .toLowerCase();

/**
 * Accept either:
 *   { "transactions": [...], "viewChat": [...] }
 * or
 *   { "emailAccess": { "transactions": [...], ... } }
 */
export const normalizeEmailAccessPayload = (data: unknown): EmailAccessMap => {
  if (!data || typeof data !== "object") return {};
  const root = data as Record<string, unknown>;
  const source =
    root.emailAccess && typeof root.emailAccess === "object"
      ? (root.emailAccess as Record<string, unknown>)
      : root;

  const out: EmailAccessMap = {};
  EMAIL_ACCESS_TABS.forEach((tab) => {
    const list = source[tab];
    if (Array.isArray(list)) {
      out[tab] = list.map((e) => normalizeEmail(String(e)));
    }
  });
  return out;
};

/** Called by ConfigProvider after fetching adminEmailAcces.json. */
export const setRemoteEmailAccess = (data: unknown): void => {
  remoteEmailAccess = normalizeEmailAccessPayload(data);
  notifyEmailAccessListeners();
};

export const isRemoteEmailAccessLoaded = (): boolean => remoteEmailAccess !== null;

/** Logged-in admin email from localStorage (profile / login payloads). */
export const getLoggedInAdminEmail = (): string => {
  try {
    const profile = JSON.parse(localStorage.getItem("profileData") || "{}");
    const login = JSON.parse(localStorage.getItem("userProfileData") || "{}");
    return normalizeEmail(
      profile?.emailId ||
        profile?.email ||
        login?.emailId ||
        login?.email ||
        login?.username ||
        ""
    );
  } catch {
    return "";
  }
};

/** Emails for a tab from remote config (empty until loaded / if tab missing). */
export const getEmailsForTab = (tab: EmailAccessTab): string[] => {
  if (!remoteEmailAccess) return [];
  return (remoteEmailAccess[tab] || []).map(normalizeEmail);
};

/** True if the given (or logged-in) email is on the allowlist for that tab. */
export const hasEmailAccess = (
  tab: EmailAccessTab,
  email?: string | null
): boolean => {
  const normalized = normalizeEmail(email) || getLoggedInAdminEmail();
  if (!normalized) return false;
  return getEmailsForTab(tab).includes(normalized);
};

/**
 * Call at the top of any component that uses hasEmailAccess during render,
 * so the UI refreshes when adminEmailAcces.json finishes loading.
 */
export const useEmailAccessConfig = (): number =>
  useSyncExternalStore(
    subscribeEmailAccess,
    getEmailAccessVersion,
    getEmailAccessVersion
  );

/**
 * Wait for adminEmailAcces.json + profile email, then return access.
 * Use this for gated pages so Access Denied is not shown too early.
 */
export const useHasEmailAccess = (
  tab: EmailAccessTab
): { ready: boolean; allowed: boolean } => {
  useEmailAccessConfig();
  const [profileVersion, setProfileVersion] = useState(0);

  useEffect(() => {
    const refresh = () => setProfileVersion((v) => v + 1);
    window.addEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
    // Profile may already be in localStorage from a prior visit / race.
    refresh();
    return () => window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
  }, []);

  const configReady = isRemoteEmailAccessLoaded();
  const email = getLoggedInAdminEmail();
  // Ready when remote lists are loaded AND we have an email to check
  // (profileData from header fetch, or userProfileData from login).
  void profileVersion;
  const ready = configReady && Boolean(email);
  const allowed = ready && hasEmailAccess(tab, email);
  return { ready, allowed };
};
